/* eslint-disable @typescript-eslint/no-explicit-any */
import * as d3 from 'd3'
import { reverse } from 'ramda'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { getRectColor } from '../../utils/charts'
import useStyle from './ChartConfusionMatrix.styles'

export interface ConfusionMatrixChartData {
  labels: string[]
  data: {
    x: string
    y: string
    value: number
    total: number
  }[]
}

export type PredictedExpectedSelection = {
  prediction?: string
  expectation?: string
}

interface ChartConfusionMatrixProps {
  data: ConfusionMatrixChartData
  onPredictedExpectedClicked?: (selection: PredictedExpectedSelection) => void
}

export const ChartConfusionMatrix = ({
  data,
  onPredictedExpectedClicked,
}: ChartConfusionMatrixProps) => {
  const { t } = useTranslation()
  const chartRef = useRef<SVGSVGElement>(null)
  const classes = useStyle()

  useEffect(() => {
    const margin = { top: 10, right: 40, bottom: 60, left: 70 }
    const svgWidth = 300
    const svgHeight = 250
    const width = svgWidth - margin.left - margin.right
    const height = svgHeight - margin.top - margin.bottom

    d3.select(chartRef.current).selectAll('*').remove()

    const svg = d3
      .select(chartRef.current)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
      .attr('class', classes.chartConfusionMatrix)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scaleBand().range([0, width]).domain(data.labels).padding(0.05)

    svg
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .attr('class', classes.xAxisText)
      .attr('dx', '-.4rem')
      .attr('dy', '.2rem')

    svg
      .append('text')
      .attr('class', classes.xAxisLabel)
      .attr('transform', `translate(${width / 2}, 0)`)
      .text(t('Predicted'))

    const y = d3
      .scaleBand()
      .range([height, 0])
      .domain(reverse(data.labels))
      .padding(0.05)

    svg
      .append('g')
      .call(d3.axisLeft(y))
      .selectAll('text')
      .attr('class', classes.yAxisText)
      .attr('dx', '.1rem')
      .attr('dy', '-0.4rem')

    svg
      .append('text')
      .attr('class', classes.yAxisLabel)
      .attr('y', height + 20)
      .attr('x', 0 - height / 2)
      .attr('dy', '1rem')
      .text(t('Expected'))

    svg
      .selectAll()
      .data(data.data)
      .join('g')
      .attr('class', 'rectGroup')
      .attr('transform', (d) => `translate(${x(d.x)},${y(d.y)})`)

    svg
      .selectAll('.rectGroup')
      .on('mouseenter', (d) => {
        d3.select(d.currentTarget).attr('class', classes.rectShadow)
      })
      .on('mouseleave', (d) => {
        d3.select(d.currentTarget).attr('class', classes.rectUnshadow)
      })
      .append('rect')
      .attr('width', x.bandwidth())
      .attr('height', y.bandwidth())
      .attr('fill', (d: any) => getRectColor(d).bg)
      .style('cursor', 'pointer')
      .classed('noselect', false)
      .on('click', (d: any, i: any) => {
        if (onPredictedExpectedClicked) {
          onPredictedExpectedClicked({
            prediction: i.x,
            expectation: i.y,
          })
        }
      })

    svg
      .selectAll('.rectGroup')
      .append('text')
      .text((d: any) => d.value)
      .attr('transform', `translate(${x.bandwidth() / 2},${y.bandwidth() / 2})`)
      .attr('fill', (d: any) => getRectColor(d).text)
      .attr('class', classes.rectText)
      .style('cursor', 'pointer')
      .on('click', (d: any, i: any) => {
        if (onPredictedExpectedClicked) {
          onPredictedExpectedClicked({
            prediction: i.x,
            expectation: i.y,
          })
        }
      })
  }, [data, classes, t, onPredictedExpectedClicked])

  return <svg ref={chartRef} />
}

export default ChartConfusionMatrix
